/** @jsx jsx */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { css, jsx } from '@emotion/core';
import { uid } from 'react-uid';
import invariant from 'tiny-invariant';
import { R400 } from '@atlaskit/theme/colors';
import { useGlobalTheme } from '@atlaskit/theme/components';
import { fontFamily as getFontFamily, gridSize as getGridSize } from '@atlaskit/theme/constants';
import { h200 } from '@atlaskit/theme/typography';
import { FormContext, IsDisabledContext } from './form';
const gridSize = getGridSize();
const fontFamily = getFontFamily();
const fieldWrapperStyles = css({
  marginTop: `${gridSize}px`
});
const labelStyles = css({
  display: 'inline-block',
  marginTop: 0,
  marginBottom: `${gridSize * 0.5}px`,
  fontFamily: `${fontFamily}`
});
const requiredIndicatorStyles = css({
  paddingLeft: `${gridSize * 0.25}px`,
  color: `${`var(--ds-text-danger, ${R400})`}`,
  fontFamily: `${fontFamily}`
}); // eslint-disable-next-line @repo/internal/react/consistent-css-prop-usage

const lightH200Styles = css(h200({
  theme: {
    mode: 'light'
  }
})); // eslint-disable-next-line @repo/internal/react/consistent-css-prop-usage

const darkH200Styles = css(h200({
  theme: {
    mode: 'dark'
  }
}));

const Label = ({
  children,
  fieldId
}) => {
  const {
    mode
  } = useGlobalTheme();
  return jsx("label", {
    css: [mode === 'light' ? lightH200Styles : darkH200Styles, labelStyles],
    id: `${fieldId}-label`,
    htmlFor: fieldId
  }, children);
};

function isEvent(event) {
  return Boolean(event && event.target);
}

function isFunction(x) {
  return typeof x === 'function';
}

/**
 * __Field id__
 *
 * A field id uses the context API. It provides the id of the field to message components. This links the message with the field of screenreaders.
 */
export const FieldId = /*#__PURE__*/React.createContext(undefined);

function usePreviousRef(current) {
  const ref = useRef(current); // will be updated on the next render

  useEffect(() => {
    ref.current = current;
  }); // return the existing current (pre render)

  return ref;
}

function isShallowEqual(previousValue, currentValue) {
  if (previousValue === currentValue) {
    return true;
  } // not checking functions


  if (typeof previousValue === 'function' && typeof currentValue === 'function') {
    return true;
  }

  if (Array.isArray(previousValue) && Array.isArray(currentValue)) {
    return JSON.stringify(previousValue) === JSON.stringify(currentValue);
  }

  if (typeof previousValue === 'object' && typeof currentValue === 'object') {
    return JSON.stringify(previousValue) === JSON.stringify(currentValue);
  }

  return false;
}

export default function Field(props) {
  const registerField = useContext(FormContext);
  const isDisabled = useContext(IsDisabledContext) || props.isDisabled || false;
  const defaultValue = isFunction(props.defaultValue) ? props.defaultValue() : props.defaultValue;
  const [state, setState] = useState({
    fieldProps: {
      onChange: () => {},
      onBlur: () => {},
      onFocus: () => {},

      /* Previously, defaultValue was being set as undefined in Field.defaultProps, which
       * effectively made it an optional prop to external consumers of Field. However the
       * prop types defined defaultValue as required, so inside the component it was not
       * valid for defaultValue to be undefined. We need to suppress the error
       * after changing defaultValue to explictly be an optional prop.
       */
      // @ts-ignore
      value: defaultValue
    },
    error: undefined,
    valid: false,
    meta: {
      dirty: false,
      dirtySinceLastSubmit: false,
      touched: false,
      valid: false,
      validating: false,
      submitting: false,
      submitFailed: false,
      error: undefined,
      submitError: undefined
    }
  });
  const latestPropsRef = usePreviousRef(props);
  const latestStateRef = usePreviousRef(state);
  /**
   * HACK: defaultValue can potentially be an array or object which cannot be
   * passed directly into a `useEffect` dependency array, since it will trigger
   * the hook every time.
   */

  const hasDefaultValueChanged = isShallowEqual(latestPropsRef.current.defaultValue, props.defaultValue);
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && !process.env.CI) {
      invariant(latestPropsRef.current.name, '@atlaskit/form: Field components have a required name prop');
    }

    function fieldStateToMeta(value = {}) {
      return {
        dirty: value.dirty || false,
        dirtySinceLastSubmit: value.dirtySinceLastSubmit || false,
        touched: value.touched || false,
        valid: value.valid || false,
        submitting: value.submitting || false,
        submitFailed: value.submitFailed || false,
        error: value.error,
        submitError: value.submitError,
        validating: !!value.validating
      };
    }

    const unregister = registerField(latestPropsRef.current.name, // @ts-ignore
    latestPropsRef.current.defaultValue, fieldState => {
      /**
       * Do not update dirtySinceLastSubmit until submission has finished.
       */
      const modifiedDirtySinceLastSubmit = fieldState.submitting ? latestStateRef.current.meta.dirtySinceLastSubmit : fieldState.dirtySinceLastSubmit;
      /**
       * Do not update submitFailed until submission has finished.
       */

      const modifiedSubmitFailed = fieldState.submitting ? latestStateRef.current.meta.submitFailed : fieldState.submitFailed;
      /**
       * Do not use submitError if the value has changed.
       */

      const modifiedSubmitError = modifiedDirtySinceLastSubmit && latestPropsRef.current.validate ? undefined : fieldState.submitError;
      const modifiedError = modifiedSubmitError || (fieldState.touched || fieldState.dirty) && fieldState.error;
      /**
       * If there has been a submit error, then use logic in modifiedError to determine validity,
       * so we can determine when there is a submit error which we do not want to display
       * because the value has been changed.
       */

      const modifiedValid = modifiedSubmitFailed ? modifiedError === undefined : fieldState.valid;

      function getTransform(eventOrValue, currentValue) {
        if (latestPropsRef.current.transform) {
          return latestPropsRef.current.transform(eventOrValue, currentValue);
        }

        if (isEvent(eventOrValue)) {
          const {
            currentTarget
          } = eventOrValue;

          if (currentTarget.type === 'checkbox') {
            //@ts-expect-error TODO Fix legit TypeScript 3.9.6 improved inference error
            if (currentTarget.checked) {
              return currentTarget.value || true;
            }

            return currentTarget.value ? undefined : false;
          } else if (currentTarget) {
            return currentTarget.value;
          }
        } else {
          return eventOrValue;
        }
      }

      setState({
        fieldProps: {
          onChange: e => {
            fieldState.change(getTransform(e, fieldState.value));
          },
          onBlur: fieldState.blur,
          onFocus: fieldState.focus,
          value: fieldState.value
        },
        error: modifiedError || undefined,

        /**
         * The following parameters are optionally typed in final-form to indicate that not all parameters need
         * to be subscribed to. We cast them as booleans (using || false), since this is what they are semantically.
         */
        valid: modifiedValid || false,
        meta: fieldStateToMeta(fieldState)
      });
    }, {
      dirty: true,
      dirtySinceLastSubmit: true,
      touched: true,
      valid: true,
      submitting: true,
      submitFailed: true,
      value: true,
      error: true,
      submitError: true,
      validating: true
    }, {
      getValidator: () => function validate(value, formState, fieldState) {
        const supplied = latestPropsRef.current.validate;

        if (supplied && fieldState) {
          return supplied(value, formState, fieldStateToMeta(fieldState));
        }
      }
    });
    return unregister;
  }, [latestPropsRef, latestStateRef, registerField, props.name, hasDefaultValueChanged]);
  const fieldId = useMemo( // eslint-disable-next-line @repo/internal/react/disallow-unstable-values
  () => props.id ? props.id : `${props.name}-${uid({
    id: props.name
  })}`, [props.id, props.name]);
  const extendedFieldProps = { ...state.fieldProps,
    name: props.name,
    isDisabled,
    isInvalid: Boolean(state.error),
    isRequired: Boolean(props.isRequired),
    'aria-invalid': state.error ? 'true' : 'false',
    'aria-labelledby': `${fieldId}-label ${fieldId}-helper ${fieldId}-valid ${fieldId}-error`,
    id: fieldId
  };
  return jsx("div", {
    css: fieldWrapperStyles
  }, props.label && jsx(Label, {
    fieldId: fieldId
  }, props.label, props.isRequired && jsx("span", {
    css: requiredIndicatorStyles,
    "aria-hidden": "true"
  }, "*")), jsx(FieldId.Provider, {
    value: fieldId
  }, props.children({
    fieldProps: extendedFieldProps,
    error: state.error,
    valid: state.valid,
    meta: state.meta
  })));
}